import { Spin, message } from 'antd';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import RequestJobPostingTemplateForm from '../../components/RequestJobPostingTemplate/RequestJobPostingTemplateForm';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { RequestJobPostingTemplateDetail } from '../../types';
import { UpdateRequestJobPostingTemplateStageSerializer } from '../../types/api';
import EditRequestJobPostingTemplateDrawer from './EditRequestJobPostingTemplateDrawer';

interface RequestJobPostingTemplateStageValue {
  id?: number;
  name: string;
  sequence: number;
  approver: number;
}

interface RequestJobPostingTemplateFormValues {
  name: string;
  templateStages: RequestJobPostingTemplateStageValue[];
}

const EditRequestJobPostingTemplatePage = () => {
  const { tenant, request_job_posting_template_id } = useTypedParams({
    tenant: String,
    request_job_posting_template_id: Number,
  });
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const [setIsDirty] = useDiscardChangesModal(form);
  const navigate = useTypedNavigate();

  const requestJobPostingTemplate = useAPIQuery(
    `/request-job-posting-templates/${request_job_posting_template_id}/`
  );

  const usersPages = useInfiniteAPIQuery([
    '/users/',
    {
      params: {
        is_active: true,
        groups__permissions__codename: 'can_approve_request_job_postings',
      },
    },
  ]);
  const users = usePrefetchPages(usersPages, (data) => data.results);

  const editMutation = useAPIMutation(
    async ({ name, templateStages }: RequestJobPostingTemplateFormValues, api) => {
      await api.patch(`/request-job-posting-templates/${request_job_posting_template_id}/`, {
        name,
        template_stages: templateStages.map((templateStage, index) => ({
          id: templateStage.id,
          name: templateStage.name,
          sequence: index,
          approver: templateStage.approver,
        })) as UpdateRequestJobPostingTemplateStageSerializer[],
      });
    },
    {
      onSuccess: () => {
        setIsDirty(false);
        message.success('Modelo atualizado com sucesso.');
        navigate({
          pathname: '/:tenant/settings/requestJobPostingTemplates/',
          params: { tenant },
          search: searchParams,
        });
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/request-job-posting-templates/');
        await queryClient.invalidateQueries(
          `/request-job-posting-templates/${request_job_posting_template_id}/`
        );
      },
    }
  );

  const deleteMutation = useAPIMutation(
    async (requestJobPostingTemplate: RequestJobPostingTemplateDetail, api) => {
      await api.delete(`/request-job-posting-templates/${requestJobPostingTemplate.id}/`);
    },
    {
      onSuccess: async () => {
        message.success('Modelo deletado');
        navigate({
          pathname: '/:tenant/settings/requestJobPostingTemplates/',
          params: { tenant },
          search: searchParams,
        });
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/request-job-posting-templates/');
      },
    }
  );

  return (
    <Spin spinning={requestJobPostingTemplate.isLoading || usersPages.isLoading}>
      <ErrorResult error={requestJobPostingTemplate.error}>
        <EditRequestJobPostingTemplateDrawer
          templateName={requestJobPostingTemplate.data?.name}
          loading={editMutation.isLoading || deleteMutation.isLoading}
          onSave={() => form.submit()}
          onDelete={() => {
            setIsDirty(false);
            if (requestJobPostingTemplate.data) {
              deleteMutation.mutate(requestJobPostingTemplate.data);
            }
          }}
        >
          <ErrorResult error={requestJobPostingTemplate.error}>
            <Form<RequestJobPostingTemplateFormValues>
              form={form}
              layout="vertical"
              onFinish={(variables) => {
                if (requestJobPostingTemplate.data) {
                  editMutation.mutate(variables);
                }
              }}
              initialValues={{
                name: requestJobPostingTemplate.data?.name,
                templateStages: requestJobPostingTemplate.data?.template_stages.map(
                  (templateStage) => ({
                    ...templateStage,
                    approver: users.find((user) => user.id === templateStage.approver.id)?.id,
                  })
                ),
              }}
            >
              <RequestJobPostingTemplateForm users={users} />
            </Form>
          </ErrorResult>
        </EditRequestJobPostingTemplateDrawer>
      </ErrorResult>
    </Spin>
  );
};

export default withRouteChange()(EditRequestJobPostingTemplatePage);
